'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useChromeT } from '../../app/i18n/useChromeT'
import { getPathLocale, localizedPath } from '../../app/i18n/supported-locales'
import { BsChatDotsFill, BsGridFill } from 'react-icons/bs'
import {
  FaBars,
  FaChevronDown,
  FaChevronUp,
  FaCog,
  FaCrown,
  FaHome,
  FaMagic,
  FaUser,
} from 'react-icons/fa'
import { HiMail } from 'react-icons/hi'
import BannedBanner from '../../component/BannedBanner'
import DiscountBanner from '../../component/DiscountBanner'
import DisputeBanner from '../../component/DisputeBanner'
import GemButton from '../../component/GemButton'
import ProfileDrawer from '../../component/ProfileDrawer'
import GemsDiscountBanner from '../../component/GemsDiscountBanner'
import LanguageSwitcher from '../../component/LanguageSwitcher'
import { useAuth } from '../../contexts/AuthContext'
import { ImageContext } from '../../contexts/ImageContext'

const Header = () => {
  const { i18n } = useTranslation()
  const t = useChromeT()
  const dropdownRef = useRef(null)

  const pathname = usePathname()
  const {
    user,
    loading,
    authHint,
    displayPlan,
    userDataLoaded,
    banned,
    disputeBanned,
  } = useAuth()

  // Optimistically treat a returning (cookie-hinted) visitor as authed while Firebase is
  // still resolving, so the header shows the account shell instead of flashing "Sign Up".
  const showAuthed = Boolean(user || (authHint && loading))
  const { setActiveButton } = useContext(ImageContext)

  const [drawerOpen, setDrawerOpen] = useState(false)
  // Large-screen (lg+) account dropdown. Smaller screens use the slide-in
  // ProfileDrawer instead (opened from the same avatar via setDrawerOpen).
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [isMoreContentVisible, setIsMoreContentVisible] = useState(false)

  const toggleMoreContent = () => {
    setIsMoreContentVisible(!isMoreContentVisible)
  }

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen)
  }

  const activeButton =
    'text-[#7a74eb] transition duration-100 ease-in-out transition-all duration-100 hover:bg-[var(--foreground-bg-color)] transition-all duration-100 rounded-full'
  const inactiveButton =
    'text-[#FEFFFF] transition duration-100 ease-in-out hover:bg-[var(--foreground-bg-color)] transition-all duration-100 rounded-full'

  // Close the large-screen dropdown when clicking anywhere outside the header.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [dropdownRef])

  // Keep the two account menus mutually exclusive across the md breakpoint: the
  // dropdown only exists at md+, the drawer only below it. If the viewport
  // crosses 768px while one is open, close it so a stale menu can't linger
  // (e.g. an open drawer reappearing on a tablet after a resize).
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mq = window.matchMedia('(min-width: 768px)')
    const sync = (e) => {
      if (e.matches) {
        setDrawerOpen(false)
      } else {
        setDropdownOpen(false)
      }
    }
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [])

  return (
    <header
      className="fixed top-0 z-50 bg-[var(--background-bg-color)] shadow-md w-full shadow-xl"
      ref={dropdownRef}
    >
      <nav className="container mx-auto px-2 py-5 items-center">
        <div className="relative flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Link
              href={
                user
                  ? '/explore'
                  : localizedPath('/', getPathLocale(pathname) || i18n.language)
              }
              onClick={() => {
                // Explore defaults to Top; the logged-out landing page still keys its
                // fresh-visitor content off the 'Trending' default.
                setActiveButton(user ? 'Top' : 'Trending')
              }}
              // py/-my cancel out: the hit area is ~36px tall (WCAG 2.5.8)
              // without adding height to the header row.
              className="flex shrink-0 items-center ml-2 py-2 -my-2"
            >
              <img
                src={'/assets/promptchan_logo.svg'}
                alt="Promptchan"
                // Intrinsic size of the SVG — reserves the right box before load
                // (no CLS). Must stay in sync with the file's viewBox, otherwise
                // `preserveAspectRatio` letterboxes the wordmark inside it.
                width="495"
                height="91"
                fetchPriority="high"
                decoding="async"
                // Height-driven, width follows the ratio. Phones span 320–430px,
                // below Tailwind's smallest breakpoint, so the size ramps fluidly
                // instead of stepping at `sm:` (16px on a 320px screen → 20px from
                // ~445px up).
                className="h-[clamp(1rem,4.5vw,1.25rem)] w-auto"
              />
            </Link>

            {/* Search bar for larger screens */}
            {/* {!safe && (
              <div className="hidden lg:block relative">
                <input
                  type="search"
                  name="search"
                  placeholder={t('search')}
                  className="border border-gray-500 rounded-full pl-10 pr-3 py-1.5 w-5/6 text-white bg-[var(--middle-bg-color)] focus:border-white focus:outline-none transition-all duration-500 ease-in-out"
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                <span className="absolute left-0 top-3 ml-3.5 text-gray-400">
                  <FaSearch />
                </span>
              </div>
            )} */}
          </div>

          <div className="absolute inset-y-0 right-0 flex items-center space-x-4 pr-2">
            {/* md+ keeps the full nav in the header row (no search here — the
                safe-country header doesn't render one). */}
            <div className="hidden md:flex space-x-1">
              <Link
                style={{ height: '2.6rem' }}
                href="/explore"
                onClick={() => {
                  setActiveButton('Top')
                }}
                className={`${
                  pathname === '/explore' ? activeButton : inactiveButton
                } font-semibold w-24 text-center py-2 rounded-xl items-center flex justify-center`}
              >
                <BsGridFill className="mr-1.5" />
                <div className="relative text-xs">{t('explore')}</div>
              </Link>
              <Link
                style={{ height: '2.6rem' }}
                href="/generate"
                className={`${
                  pathname === '/generate' ? activeButton : inactiveButton
                } font-semibold w-24 text-center py-2 rounded-xl items-center flex justify-center`}
              >
                <FaMagic className="mr-1.5" />
                <div className="relative text-xs">{t('create')}</div>
              </Link>

              <Link
                style={{ height: '2.6rem' }}
                href="/chat"
                className={`${
                  pathname.startsWith('/chat') ? activeButton : inactiveButton
                } font-semibold w-24 text-center py-2 rounded-xl items-center flex justify-center`}
              >
                <BsChatDotsFill className="mr-1.5" />
                <div className="relative text-xs">{t('chat')}</div>
              </Link>
            </div>

            <div className="flex space-x-2">
              {showAuthed ? (
                <div className="flex items-center justify-center">
                  <GemButton style={{ height: '2.6rem' }} />

                  {/* md+: account dropdown trigger. Matches where the nav
                      links live, so tablets get the same header as desktop. */}
                  <button
                    onClick={toggleDropdown}
                    aria-label="Open menu"
                    aria-expanded={dropdownOpen}
                    className="bg-transparent font-semibold w-10 h-10 hidden md:flex items-center justify-center rounded-full border border-gray-500 ml-2 hover:bg-[var(--secondary-bg-color)]"
                  >
                    <FaUser className="text-white" />
                  </button>

                  {/* Phones (< md): hamburger opens the slide-in profile
                      drawer — clearer as a menu trigger than the person icon,
                      and the only place the nav links exist at this width. */}
                  <button
                    onClick={() => setDrawerOpen(true)}
                    aria-label="Open menu"
                    aria-expanded={drawerOpen}
                    className="bg-transparent font-semibold w-10 h-10 flex md:hidden items-center justify-center rounded-full border border-gray-500 ml-2 hover:bg-[var(--secondary-bg-color)]"
                  >
                    <FaBars className="text-white text-lg" />
                  </button>

                  {/* Dropdown panel — anchored to the right cluster. */}
                  {dropdownOpen && (
                    <div className="absolute right-0 top-1 mt-10 hidden w-48 rounded-md bg-[var(--foreground-bg-color)] text-white shadow-lg z-40 md:block lg:mt-12">
                      <Link
                        href={`/profile/${user.uid}`}
                        className={`flex items-center px-4 py-2 text-sm font-semibold text-white text-right hover:bg-[var(--secondary-bg-color)] ${
                          pathname.startsWith('/profile/') &&
                          'bg-[var(--secondary-bg-color)]'
                        }`}
                        onClick={toggleDropdown}
                      >
                        <FaHome className="mr-2" />
                        {t('profile')}
                      </Link>

                      <Link
                        href="/settings"
                        className={`flex items-center px-4 py-2 text-sm font-semibold text-white text-right hover:bg-[var(--secondary-bg-color)] ${
                          pathname === '/settings' &&
                          'bg-[var(--secondary-bg-color)]'
                        }`}
                        onClick={toggleDropdown}
                      >
                        <FaCog className="mr-2" />
                        {t('accountSettings')}
                      </Link>

                      <Link
                        href={`/contact`}
                        prefetch={false}
                        className="flex items-center px-4 py-2 text-sm font-semibold text-white text-right hover:bg-[var(--secondary-bg-color)]"
                        onClick={toggleDropdown}
                      >
                        <HiMail className="mr-2" />
                        {t('contact')}
                      </Link>

                      <a
                        href="https://aiffiliate.io/"
                        className="flex items-center px-4 py-2 text-sm font-semibold text-white text-right hover:bg-[var(--secondary-bg-color)] border-t border-gray-700"
                        onClick={toggleDropdown}
                        rel="noopener noreferrer"
                      >
                        {/* Gold matches the Pro tier's gradient mid-stop. */}
                        <FaCrown className="mr-2 text-[#F2AF50]" />
                        {t('affiliate')}
                      </a>

                      <button
                        onClick={toggleMoreContent}
                        className="block px-4 py-2 text-sm font-semibold text-white items-center justify-center flex hover:bg-[var(--secondary-bg-color)] w-full"
                      >
                        {isMoreContentVisible ? (
                          <FaChevronUp />
                        ) : (
                          <FaChevronDown />
                        )}
                      </button>
                      {isMoreContentVisible && (
                        <>
                          <Link
                            href="/privacypolicy"
                            prefetch={false}
                            className={`flex items-center px-4 py-2 text-sm font-semibold text-white text-right hover:bg-[var(--secondary-bg-color)] ${
                              pathname === '/privacypolicy' &&
                              'bg-[var(--secondary-bg-color)]'
                            }`}
                            onClick={toggleDropdown}
                          >
                            {t('privacyPolicy')}
                          </Link>
                          <a
                            href="https://promptchan.com/termsofservice"
                            className="flex items-center px-4 py-2 text-sm font-semibold text-white text-right hover:bg-[var(--secondary-bg-color)]"
                            onClick={toggleDropdown}
                            rel="noopener noreferrer"
                          >
                            {t('termsOfService')}
                          </a>
                          <Link
                            href="/refund"
                            prefetch={false}
                            className={`flex items-center px-4 py-2 text-sm font-semibold text-white text-right hover:bg-[var(--secondary-bg-color)] ${
                              pathname === '/refund' &&
                              'bg-[var(--secondary-bg-color)]'
                            }`}
                            onClick={toggleDropdown}
                          >
                            {t('refundpolicy')}
                          </Link>
                          <Link
                            href="/dmcapolicy"
                            prefetch={false}
                            className={`flex items-center px-4 py-2 text-sm font-semibold text-white text-right hover:bg-[var(--secondary-bg-color)] ${
                              pathname === '/dmcapolicy' &&
                              'bg-[var(--secondary-bg-color)]'
                            }`}
                            onClick={toggleDropdown}
                          >
                            {t('dmcapolicy')}
                          </Link>
                          <Link
                            href="/contentmoderationpolicy"
                            prefetch={false}
                            className={`flex items-center px-4 py-2 text-sm font-semibold text-white text-right hover:bg-[var(--secondary-bg-color)] ${
                              pathname === '/contentmoderationpolicy' &&
                              'bg-[var(--secondary-bg-color)]'
                            }`}
                            onClick={toggleDropdown}
                          >
                            {t('moderationpolicy')}
                          </Link>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  href="/signup"
                  aria-label="Sign up for Promptchan"
                  className={`text-white ${
                    pathname === '/signup' &&
                    'bg-[var(--foreground-bg-color)] text-white'
                  } font-semibold lg:w-28 w-24 text-center py-2 rounded-xl ml-4 bg-[var(--main-bg-color)] hover:scale-105 transition-transform duration-200`}
                >
                  {t('signUp')}
                </Link>
              )}
              {!showAuthed && (
                <div className="flex items-center space-x-1">
                  <LanguageSwitcher />
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Second-row nav, phones only (below md) — now for everyone, not just
          anonymous visitors, so signed-in users get full-size tap targets
          instead of links squeezed into the row above. From md up the same
          links sit in the header row itself. */}
      <div className="md:hidden flex space-x-2 pb-1 justify-center items-center">
        {/* No search here — see the main header. This variant never rendered
            one anyway; the commented-out input and toggle are gone with it. */}
        <div className="flex">
          <Link
            href="/explore"
            onClick={() => {
              setActiveButton('Top')
            }}
            className={`${
              pathname === '/explore' ? activeButton : inactiveButton
            } font-semibold text-center py-2 rounded-xl flex items-center justify-center flex-col w-16`}
          >
            <BsGridFill className="h-6" />
            <div className="relative text-xs">{t('explore')}</div>
          </Link>
          <Link
            href="/generate"
            className={`${
              pathname === '/generate' ? activeButton : inactiveButton
            } font-semibold text-center py-2 rounded-xl flex items-center justify-center flex-col w-16`}
          >
            <FaMagic className="h-6" />
            <div className="relative text-xs">{t('create')}</div>
          </Link>

          <Link
            href="/chat"
            className={`${
              pathname.startsWith('/chat') ? activeButton : inactiveButton
            } font-semibold text-center py-2 rounded-xl flex items-center justify-center flex-col w-16`}
          >
            <BsChatDotsFill className="h-6" />
            <div className="relative text-xs">{t('chat')}</div>
          </Link>
        </div>
      </div>

      {/* Banners */}
      {!(
        pathname.startsWith('/upgrade') ||
        pathname.startsWith('/gems') ||
        pathname.startsWith('/article')
      ) && (
        <>
          {(() => {
            // Exceptional-state banners need server-verified flags (no safe cached
            // hint), so they wait for the real data to load.
            if (userDataLoaded) {
              if (banned) return <BannedBanner />
              if (disputeBanned) return <DisputeBanner />
            }

            // DISPLAY ONLY — the plan banner renders from the remembered plan
            // (`displayPlan`) so it doesn't pop in after auth resolves; it self-corrects
            // the moment the real plan loads. Never gates anything.
            if (showAuthed && displayPlan === 'Free') return <DiscountBanner />
            if (showAuthed && displayPlan)
              return <GemsDiscountBanner plan={displayPlan} />

            return null
          })()}
        </>
      )}

      <ProfileDrawer
        safe
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />
    </header>
  )
}

export default Header
